import React, { useState } from 'react';
import {
  Clock,
  Phone,
  MessageSquare,
  CheckCircle2,
  Plus
} from 'lucide-react';
import type { FollowupItem } from './types';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';

/**
 * A follow-up row used to say only "Quotation follow-up" in the same muted grey
 * as the due time, so a rep scanning the list could not tell at a glance whether
 * they were chasing a lead, a quotation or an unpaid invoice — the one thing
 * that decides what they say on the call. The source type was already resolved
 * per row (`relatedType`); it was only ever read for navigation.
 */
const FOLLOWUP_SOURCE_TONE: Record<FollowupItem['relatedType'], BadgeTone> = {
  lead: 'neutral',
  deal: 'info',
  quotation: 'primary',
  proforma: 'warning',
  invoice: 'success',
  sample: 'neutral',
};

export function FollowupSourceTag({ type }: { type: FollowupItem['relatedType'] }) {
  return (
    <Badge tone={FOLLOWUP_SOURCE_TONE[type] ?? 'neutral'} className="font-semibold uppercase tracking-wide">
      {type}
    </Badge>
  );
}

interface FollowupCenterProps {
  followups: FollowupItem[];
  onComplete: (id: string) => void;
  onNewFollowup: () => void;
  onOpenFollowup: (item: FollowupItem) => void;
  /** The logged-in rep's own name, used in the WhatsApp message template. */
  repName?: string;
}

export const FollowupCenter: React.FC<FollowupCenterProps> = ({
  followups,
  onComplete,
  onNewFollowup,
  onOpenFollowup,
  repName = '',
}) => {
  const [activeTab, setActiveTab] = useState<'overdue' | 'today' | 'tomorrow' | 'upcoming'>('today');

  const filtered = followups.filter((f) => {
    if (activeTab === 'overdue') return f.isOverdue || f.dueDate.toLowerCase().includes('overdue');
    if (activeTab === 'today') return f.dueDate.toLowerCase().includes('today') && !f.isOverdue;
    if (activeTab === 'tomorrow') return f.dueDate.toLowerCase().includes('tomorrow') && !f.isOverdue;
    return !f.dueDate.toLowerCase().includes('today') && !f.isOverdue;
  });

  const overdueCount = followups.filter((f) => f.isOverdue || f.dueDate.toLowerCase().includes('overdue')).length;
  const todayCount = followups.filter((f) => f.dueDate.toLowerCase().includes('today') && !f.isOverdue).length;
  const tomorrowCount = followups.filter((f) => f.dueDate.toLowerCase().includes('tomorrow') && !f.isOverdue).length;
  const upcomingCount = followups.filter((f) => !f.dueDate.toLowerCase().includes('today') && !f.isOverdue).length;

  return (
    <div id="followup-center" className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs scroll-mt-6">
      {/* Header & Tabs */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
              <Clock className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Follow-ups Command Center
              </span>
              <div className="text-data-xs text-on-surface-subtle">Scheduled touchpoints & client engagement</div>
            </div>
          </div>

          <button
            type="button"
            onClick={onNewFollowup}
            className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary-container px-2.5 py-1 text-body-sm font-semibold text-primary transition hover:bg-primary-container active:scale-95"
          >
            <Plus className="h-3 w-3" />
            <span>Schedule</span>
          </button>
        </div>

        {/* Tab Strip */}
        <div className="mt-3 flex items-center gap-1 border-b border-border pb-2">
          <button
            type="button"
            onClick={() => setActiveTab('overdue')}
            className={`relative rounded-md px-2.5 py-1 text-body-sm font-semibold transition ${
              activeTab === 'overdue'
                ? 'bg-danger/5 text-danger-text'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Overdue
            {overdueCount > 0 && (
              <span className="ml-1.5 rounded-full bg-destructive px-1.5 py-0.5 text-data-xs font-semibold text-on-primary">
                {overdueCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('today')}
            className={`relative rounded-md px-2.5 py-1 text-body-sm font-semibold transition ${
              activeTab === 'today'
                ? 'bg-primary-container text-primary'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Today
            {todayCount > 0 && (
              <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-data-xs font-semibold text-on-primary">
                {todayCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('tomorrow')}
            className={`relative rounded-md px-2.5 py-1 text-body-sm font-semibold transition ${
              activeTab === 'tomorrow'
                ? 'bg-surface-container text-on-surface'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Tomorrow
            {tomorrowCount > 0 && (
              <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-data-xs font-semibold text-on-primary">
                {tomorrowCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('upcoming')}
            className={`relative rounded-md px-2.5 py-1 text-body-sm font-semibold transition ${
              activeTab === 'upcoming'
                ? 'bg-surface-container text-on-surface'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Upcoming
            {upcomingCount > 0 && (
              <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-data-xs font-semibold text-on-primary">
                {upcomingCount}
              </span>
            )}
          </button>
        </div>

        {/* List of Follow-up Rows */}
        <div className="my-2 flex flex-col divide-y divide-border">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-body-sm text-on-surface-variant">
              No {activeTab} follow-ups scheduled. Good job!
            </div>
          ) : (
            filtered.map((item) => (
              <div
                key={item.id}
                onClick={() => onOpenFollowup(item)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onOpenFollowup(item);
                }}
                role="button"
                tabIndex={0}
                title="Open this client and its follow-ups"
                className="flex items-center justify-between py-3 transition hover:bg-surface-variant/70 px-2 rounded-lg"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-body-sm text-on-surface">
                      {item.company}
                    </span>
                    <span className="text-data-xs text-on-surface-subtle">({item.contactName})</span>
                    {item.priority === 'high' && (
                      <span className="rounded bg-danger/5 px-1 py-0.5 text-data-xs font-semibold uppercase text-danger-text border border-danger/30">
                        HIGH
                      </span>
                    )}
                  </div>

                  <p className="mt-0.5 text-body-sm text-on-surface-variant line-clamp-1">{item.note}</p>

                  <div className="mt-1 flex items-center gap-3 text-data-xs text-on-surface-subtle">
                    <span className="inline-flex items-center gap-1 font-medium text-on-surface-variant">
                      <Clock className="h-3 w-3 text-on-surface-subtle" />
                      {item.dueDate && item.dueDate !== 'Today' ? `${item.dueDate} · ` : ''}{item.dueTime}
                    </span>
                    <span>•</span>
                    <FollowupSourceTag type={item.relatedType} />
                    <span>{item.relatedEntity}</span>
                  </div>
                </div>

                {/* Quick Action Buttons */}
                <div className="flex items-center gap-1 shrink-0">
                  {/* Call */}
                  <a
                    onClick={(event) => event.stopPropagation()}
                    href={`tel:${item.phone}`}
                    title={`Call ${item.phone}`}
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-on-surface-variant hover:bg-success/10 hover:text-success-text hover:border-success/30 transition"
                  >
                    <Phone className="h-3.5 w-3.5" />
                  </a>

                  {/* WhatsApp */}
                  <a
                    onClick={(event) => event.stopPropagation()}
                    href={`https://wa.me/91${item.phone.replace(/\D/g, '')}?text=${encodeURIComponent(
                      `Hello, this is${repName ? ` ${repName}` : ''} from RBM Oil Corporation. Following up regarding ${item.relatedEntity}. Please let me know if you would like me to share technical specifications or quotation details.`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Send WhatsApp Message"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-on-surface-variant hover:bg-success/10 hover:text-success-text hover:border-success/30 transition"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                  </a>

                  {/* Complete Checkmark */}
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onComplete(item.id); }}
                    title="Mark Follow-up as Completed"
                    className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-on-surface-variant hover:bg-primary-container hover:text-primary hover:border-primary/30 transition"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-2 rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Actionable touchpoints:</span>
        <span className="font-semibold text-on-surface">{followups.length} scheduled</span>
      </div>
    </div>
  );
};
